/**
 * Edge propagation delays -- where a bank states how fast a failure
 * travels one edge (0182, brief two section 3.2).
 *
 * WHY THIS IS A PAGE AND NOT A DRAWER FIELD. Of the four junctions that
 * carry impact, only IBS -> channel has an admin drawer; the other
 * three are connector- or CSV-managed. The two that matter most for
 * propagation -- service -> service and service -> vendor -- are
 * exactly the two with no form, so a drawer-only answer would leave the
 * important edges undeclarable and the feature would read as shipped
 * while being unusable.
 *
 * Undeclared is the honest default and is shown as such: the row says
 * the tenant hop delay applies, rather than pre-filling 22 and inviting
 * someone to accept a number nobody measured. Clearing is offered
 * separately from setting 0, because "we have not measured this" and
 * "it crosses instantly" are different claims.
 */
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "shared/api/client";
import type { ApiError } from "shared/api/client";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { PathTrail } from "shared/components/PathTrail";
import { hopDelayFrom, useTwin3DSettings } from "shared/twin3d/viewSettings";
import { useToast } from "shared/toast/ToastProvider";

interface EdgeRow {
  edge_kind: string;
  id: string;
  from_label: string;
  to_label: string;
  propagation_delay_minutes: number | null;
}

/**
 * Round 10 D2 -- the two ends of each edge, as catalogue keys.
 *
 * These were hard-coded English with a Unicode arrow: neither
 * translated nor mirrored, on a page whose whole subject is the
 * direction impact travels. Found by the arrow rule this round added,
 * which is the point of having one.
 */
const KIND_HOPS: Record<string, readonly string[]> = {
  ibs_channel: ["channel", "ibs"],
  ibs_service: ["ibs", "service"],
  service_vendor: ["service", "vendor"],
  service_dependency: ["service", "service"],
};

export function EdgeDelaysView(): JSX.Element {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { push } = useToast();
  const [filter, setFilter] = useState("");
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  // Round 10 A5. This page deferred to "the tenant hop delay" four
  // times and never printed it, so the fallback every undeclared edge
  // falls back TO was the one number the reader could not see.
  const { data: viewSettings } = useTwin3DSettings();
  const tenantHop = hopDelayFrom(viewSettings);

  const edges = useQuery<EdgeRow[], ApiError>({
    queryKey: ["admin", "edge-delays"] as const,
    queryFn: () => api.get<EdgeRow[]>("/v1/admin/foundation/edges"),
    // A seat that may not read this is refused once, not retried at.
    retry: false,
  });

  const save = useMutation({
    mutationFn: (args: { row: EdgeRow; value: number | null }) =>
      api.patch<EdgeRow>(
        `/v1/admin/foundation/edges/${args.row.edge_kind}/${args.row.id}/propagation-delay`,
        { propagation_delay_minutes: args.value },
      ),
    onSuccess: (_d, args) => {
      void qc.invalidateQueries({ queryKey: ["admin", "edge-delays"] });
      // Drop the draft for the saved row without a dynamic delete:
      // the server value is now authoritative for it.
      setDrafts((d) =>
        Object.fromEntries(Object.entries(d).filter(([k]) => k !== args.row.id)),
      );
      push({
        tone: "success",
        message:
          args.value === null
            ? t("screens.edgeDelaysView.cleared", { minutes: tenantHop })
            : t("screens.edgeDelaysView.saved", { minutes: args.value }),
      });
    },
  });

  const rows = useMemo(() => {
    const all = edges.data ?? [];
    const q = filter.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (e) =>
        e.from_label.toLowerCase().includes(q) ||
        e.to_label.toLowerCase().includes(q) ||
        e.edge_kind.includes(q),
    );
  }, [edges.data, filter]);

  const declared = (edges.data ?? []).filter(
    (e) => e.propagation_delay_minutes !== null,
  ).length;

  return (
    <article data-testid="edge-delays-view" className="max-w-4xl space-y-5">
      <header>
        <h1 className="flex items-center gap-2 font-display text-2xl">
          <MarsadIcon name="cascade" className="text-brand-brassDark" />
          {t("screens.edgeDelaysView.heading")}
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          {t("screens.edgeDelaysView.blurb", { minutes: tenantHop })}
        </p>
        <p
          data-testid="edge-delays-blank-is-not-zero"
          className="mt-1 text-xs text-slate-600"
        >
          {t("screens.edgeDelaysView.blankIsNotZero")}
        </p>
        {/* Round 12 H4 -- a permission refusal rendered as a number.
            The read is admin-gated and this page is reachable by a seat
            that is not one, so on that seat the query 403s, `data` is
            undefined, and the header printed "0 of 0 edges carry a
            declared delay". Nothing on the page had failed to load as
            far as the reader could tell: it said the estate has no
            edges, which is a claim about the bank rather than about
            the reader.

            The same class this round's B1 is about, one surface over --
            a figure computed from an empty answer and printed as
            though the answer were the estate's. */}
        {edges.isError ? (
          <p
            data-testid="edge-delays-refused"
            className="mt-1 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900"
          >
            {edges.error?.status === 403
              ? t("screens.edgeDelaysView.needsAdmin")
              : t("screens.edgeDelaysView.readFailed")}
          </p>
        ) : edges.isPending ? (
          <p data-testid="edge-delays-loading" className="mt-1 text-xs text-slate-500">
            {t("common.loading")}
          </p>
        ) : (
          <p
            data-testid="edge-delays-declared"
            data-declared={declared}
            className="mt-1 text-xs text-slate-500"
          >
            {t("screens.edgeDelaysView.declaredCount", {
              // `count` drives the plural; `declared` is what is rendered.
              count: declared,
              declared,
              total: (edges.data ?? []).length,
            })}
          </p>
        )}
        {/* Round 10 A5: the page had no way out of it. One link to the
            scene these figures move, one to the row the fallback lives
            in -- both reachable by the administrator already here. */}
        <p className="mt-1 flex flex-wrap gap-3 text-xs">
          <Link
            data-testid="edge-delays-to-cascade"
            to="/twin/cascade"
            className="text-brand-brassDark underline decoration-dotted underline-offset-2"
          >
            {t("screens.edgeDelaysView.seeCascade")}
          </Link>
          <Link
            data-testid="edge-delays-to-settings"
            to="/admin/settings#twin3d"
            className="text-brand-brassDark underline decoration-dotted underline-offset-2"
          >
            {t("screens.edgeDelaysView.tenantHopSetting", { minutes: tenantHop })}
          </Link>
        </p>
      </header>

      <input
        data-testid="edge-delays-filter"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder={t("screens.edgeDelaysView.filter")}
        className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
      />

      {edges.isLoading && (
        <p className="text-sm text-slate-500">{t("common.loading")}</p>
      )}

      <ul className="space-y-1">
        {rows.map((row) => {
          const draft = drafts[row.id];
          const current =
            row.propagation_delay_minutes === null
              ? ""
              : String(row.propagation_delay_minutes);
          const text = draft ?? current;
          const parsed = text.trim() === "" ? null : Number(text);
          const pathLabel = `${row.from_label} \u2192 ${row.to_label}`;
          const valid = parsed === null || (Number.isInteger(parsed) && parsed >= 0);
          // Compare against what the server holds, not merely "has a
          // draft": retyping the same number is not a declaration.
          const changed = text.trim() !== current;
          return (
            <li
              key={row.id}
              data-testid="edge-delay-row"
              data-edge-kind={row.edge_kind}
              data-declared={row.propagation_delay_minutes ?? ""}
              className="flex flex-wrap items-baseline justify-between gap-2 rounded border border-slate-200 bg-white px-2 py-1 text-xs"
            >
              <span className="min-w-0">
                <span className="font-medium text-slate-900">{pathLabel}</span>
                {" · "}
                <span className="ms-2 text-slate-500">
                  <PathTrail
                    hops={KIND_HOPS[row.edge_kind] ?? []}
                    keyPrefix="screens.edgeDelaysView.hop"
                    fallback={row.edge_kind}
                    className="inline"
                  />
                </span>
              </span>
              <span className="flex items-center gap-1">
                {row.propagation_delay_minutes === null && draft === undefined && (
                  <span data-testid="edge-delay-undeclared" className="text-slate-500">
                    {t("screens.edgeDelaysView.undeclared", { minutes: tenantHop })}
                  </span>
                )}
                <input
                  data-testid="edge-delay-input"
                  value={text}
                  inputMode="numeric"
                  onChange={(e) =>
                    setDrafts((d) => ({ ...d, [row.id]: e.target.value }))
                  }
                  className="w-16 rounded border border-slate-300 px-1 py-0.5 text-end font-mono"
                />
                <span className="text-slate-500">
                  {t("screens.edgeDelaysView.minutes")}
                </span>
                <button
                  type="button"
                  data-testid="edge-delay-save"
                  disabled={!valid || !changed}
                  /* Round 10 A5: the walkthrough read a permanently
                     greyed Save as "no declaration can be completed".
                     It is disabled because nothing has been typed yet,
                     which is correct and was invisible -- so it says
                     which of the two it is. */
                  title={
                    !valid
                      ? t("screens.edgeDelaysView.saveInvalid")
                      : changed
                        ? t("screens.edgeDelaysView.saveReady")
                        : t("screens.edgeDelaysView.saveUnchanged")
                  }
                  onClick={() => save.mutate({ row, value: parsed })}
                  className="rounded border border-brand-brass/40 bg-brand-brass/5 px-1.5 py-0.5 font-medium text-brand-brassDark disabled:opacity-40"
                >
                  {t("common.save")}
                </button>
                {/* Clearing is a different claim from declaring zero,
                    and the module docstring has promised it was offered
                    separately since the page shipped. It was not. */}
                {row.propagation_delay_minutes !== null && (
                  <button
                    type="button"
                    data-testid="edge-delay-clear"
                    onClick={() => save.mutate({ row, value: null })}
                    title={t("screens.edgeDelaysView.clearHint", {
                      minutes: tenantHop,
                    })}
                    className="rounded border border-slate-300 px-1.5 py-0.5 font-medium text-slate-600"
                  >
                    {t("screens.edgeDelaysView.clear")}
                  </button>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </article>
  );
}
